import { useState } from 'react';

import { ERROR_TOAST_CONFIG } from '../constants/errorToastConfig';
import type { ErrorToastCode } from '../types/errorToastConfig';
import { buildDownloadUrl } from '../utils/downloadUtils';
import { capturePageAction } from '../utils/newrelic';

import { useToast } from './useToast';

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return JSON.stringify(error) ?? 'Unknown error';
};

export const useDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { showToast } = useToast();

  const downloadFile = async (
    artifactKey: string,
    version: string,
  ): Promise<void> => {
    setIsDownloading(true);
    const startTime = performance.now();

    try {
      const downloadUrl = buildDownloadUrl(artifactKey, version);

      // Queries the download link to see if it is valid and accessible, does not immediately start downloading
      const response = await fetch(downloadUrl, {
        method: 'HEAD',
      });

      const latencyMs = Math.round(performance.now() - startTime);

      if (!response.ok) {
        // Track failed download check in New Relic
        capturePageAction('download_check', {
          artifactKey,
          version,
          downloadUrl,
          status: 'error',
          statusCode: response.status,
          latencyMs,
        });

        if (ERROR_TOAST_CONFIG[response.status as ErrorToastCode]) {
          showToast(
            ERROR_TOAST_CONFIG[response.status as ErrorToastCode].message,
          );
        } else {
          showToast(ERROR_TOAST_CONFIG.default.message);
        }

        return;
      }

      // Track successful download check in New Relic
      const fileSize = response.headers.get('content-length');

      capturePageAction('download_check', {
        artifactKey,
        version,
        downloadUrl,
        status: 'success',
        statusCode: response.status,
        latencyMs,
        fileSizeBytes: fileSize ? parseInt(fileSize, 10) : null,
      });

      // File exists: trigger download using direct navigation for better performance
      window.location.href = downloadUrl;
    } catch (error: unknown) {
      const latencyMs = Math.round(performance.now() - startTime);
      const errorMessage = getErrorMessage(error);

      // Track network error in New Relic
      capturePageAction('download_failed_unknown', {
        artifactKey,
        version,
        status: 'error',
        statusCode: 0,
        latencyMs,
        errorMessage,
      });

      // Network error or other issues
      showToast(ERROR_TOAST_CONFIG.default.message);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadFile,
    isDownloading,
  };
};
